import React, { useState, useEffect, useRef, useCallback } from 'react';
import { gameAPI } from '../../services/gameAPI';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import ReviewStatusPanel from './ReviewStatusPanel';
import AdminImageAudit from './AdminImageAudit';
import AdminGenerationFailures from './AdminGenerationFailures';
import { Loader2, X, Info, Activity, CheckCircle, XCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { getUserDisplayLabel } from '../../utils/userDisplay';

// =============================================================================
// TAB CONSTANTS
// =============================================================================

const TABS = {
  SUMMARY: 'summary',
  REVIEW: 'review',
  IDENTITY: 'identity',
  LOGIC: 'logic',
  CIPHER: 'cipher',
  IMAGES: 'images',
  FAILURES: 'failures',
  SESSIONS: 'sessions',
  DIAGNOSTICS: 'diagnostics',
};

const TAB_ITEMS = [
  { id: TABS.SUMMARY, label: 'Summary/Generation', icon: Info, accent: 'amber' },
  { id: TABS.REVIEW, label: 'Review', icon: AlertTriangle, accent: 'amber' },
  { id: TABS.IDENTITY, label: 'Identity', icon: Info, accent: 'cyan' },
  { id: TABS.LOGIC, label: 'Logic', icon: CheckCircle, accent: 'emerald' },
  { id: TABS.CIPHER, label: 'Cipher', icon: XCircle, accent: 'purple' },
  { id: TABS.IMAGES, label: 'Images', icon: Info, accent: 'orange' },
  { id: TABS.FAILURES, label: 'Failures', icon: AlertTriangle, accent: 'red' },
  { id: TABS.SESSIONS, label: 'Sessions', icon: Activity, accent: 'sky' },
  { id: TABS.DIAGNOSTICS, label: 'Diagnostics', icon: Activity, accent: 'cyan' },
];

const getTabClasses = (tab, activeTab) => {
  const activeClasses = {
    amber: 'text-amber-400 border-b-2 border-amber-400 bg-amber-900/10',
    cyan: 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-900/10',
    emerald: 'text-emerald-400 border-b-2 border-emerald-400 bg-emerald-900/10',
    purple: 'text-purple-400 border-b-2 border-purple-400 bg-purple-900/10',
    orange: 'text-orange-400 border-b-2 border-orange-400 bg-orange-900/10',
    red: 'text-red-400 border-b-2 border-red-400 bg-red-900/10',
    sky: 'text-sky-400 border-b-2 border-sky-400 bg-sky-900/10',
  };

  return activeTab === tab.id
    ? activeClasses[tab.accent] || activeClasses.amber
    : 'text-gray-400 hover:text-white hover:bg-gray-800/50 border-b-2 border-transparent';
};

// =============================================================================
// GENERATION INFO COMPONENTS
// =============================================================================

const getThemeColors = (theme) => {
  const colors = {
    noir: 'bg-gray-800 text-gray-100',
    '90s_nyc': 'bg-orange-600 text-white',
    miami_vice: 'bg-pink-500 text-white'
  };
  return colors[theme] || colors.noir;
};

const formatThemeName = (theme) => {
  const names = {
    noir: 'Noir',
    '90s_nyc': '90s NYC',
    miami_vice: 'Miami Vice'
  };
  return names[theme] || theme;
};

/**
 * CaseOverviewSection - Basic case info (victim, theme, difficulty, etc.)
 */
const CaseOverviewSection = ({ data }) => (
  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
    <h3 className="text-lg font-semibold text-white mb-3">Case Overview</h3>
    <div className="grid grid-cols-2 gap-4 text-sm">
      <div>
        <span className="text-gray-400">Victim:</span>
        <span className="ml-2 text-white">{data.victim?.name}</span>
      </div>
      <div>
        <span className="text-gray-400">Theme:</span>
        <Badge className={`ml-2 ${getThemeColors(data.theme)}`}>
          {formatThemeName(data.theme)}
        </Badge>
      </div>
      <div>
        <span className="text-gray-400">Difficulty:</span>
        <span className="ml-2 text-white capitalize">{data.difficulty}</span>
      </div>
      <div>
        <span className="text-gray-400">Mode:</span>
        <span className="ml-2 text-white">{data.generation_mode || 'unknown'}</span>
      </div>
      <div>
        <span className="text-gray-400">Crime Scene Suspects:</span>
        <span className={`ml-2 font-semibold ${
          data.crime_scene_suspect_count > 2 ? 'text-red-400' : 'text-green-400'
        }`}>{data.crime_scene_suspect_count}</span>
      </div>
      <div>
        <span className="text-gray-400">Case ID:</span>
        <span className="ml-2 text-gray-500 text-xs font-mono">{data.case_id?.slice(0, 8)}...</span>
      </div>
    </div>
  </div>
);

/**
 * SuspectLocationsSection - Shows where each suspect is located
 */
const SuspectLocationsSection = ({ suspects }) => (
  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
    <h3 className="text-lg font-semibold text-white mb-3">Suspect Locations</h3>
    <div className="space-y-2">
      {suspects?.map((suspect) => (
        <div
          key={suspect.id}
          className={`flex items-center justify-between p-2 rounded ${
            suspect.primary_location === 'crime_scene' ? 'bg-amber-900/20 border border-amber-700/50' : 'bg-gray-700/30'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="text-white">{suspect.name}</span>
            {suspect.is_killer && (
              <Badge className="bg-red-600 text-white text-xs">KILLER</Badge>
            )}
          </div>
          <Badge variant="outline" className="text-gray-300 border-gray-600 font-mono text-xs">
            {suspect.primary_location || 'NOT SET'}
          </Badge>
        </div>
      ))}
    </div>
  </div>
);

/**
 * GenerationLogSection - Shows generation log details, reconciliation, warnings
 */
const GenerationLogSection = ({ log }) => {
  if (!log) return null;

  return (
    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
      <h3 className="text-lg font-semibold text-white mb-3">Generation Log</h3>

      {/* ID Match Summary */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          {log.has_fallbacks ? (
            <XCircle className="w-5 h-5 text-red-400" />
          ) : (
            <CheckCircle className="w-5 h-5 text-green-400" />
          )}
          <span className="text-sm text-gray-300">
            ID Match Rate: <span className="font-mono">{log.id_match_rate}</span>
          </span>
        </div>
        <div className="text-sm text-gray-400">
          Intended CS suspects: {log.crime_scene_suspects_intended}
          {log.crime_scene_suspects_actual !== undefined && (
            <span className="ml-2">
              Actual: <span className={
                log.crime_scene_suspects_actual > log.crime_scene_suspects_intended
                  ? 'text-red-400' : 'text-green-400'
              }>{log.crime_scene_suspects_actual}</span>
            </span>
          )}
        </div>
      </div>

      {/* Location Reconciliation */}
      {(log.reconciled_count > 0 || log.ambiguous_suspects?.length > 0 || log.orphan_suspects?.length > 0) && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-blue-400 mb-2 flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Location Reconciliation
            {log.reconciled_count > 0 && (
              <span className="text-xs bg-blue-800 px-1.5 py-0.5 rounded">{log.reconciled_count} fixed</span>
            )}
            {log.ambiguous_suspects?.length > 0 && (
              <span className="text-xs bg-amber-800 px-1.5 py-0.5 rounded">{log.ambiguous_suspects.length} ambiguous</span>
            )}
            {log.orphan_suspects?.length > 0 && (
              <span className="text-xs bg-red-800 px-1.5 py-0.5 rounded">{log.orphan_suspects.length} orphan</span>
            )}
          </h4>
          <div className="bg-blue-900/20 rounded p-2 border border-blue-700/50 space-y-1">
            {log.reconciliation_details?.map((detail, idx) => (
              <div key={idx} className="text-sm">
                <span className="text-blue-300">{detail.suspect_name}: </span>
                {detail.reconciled_from && (
                  <>
                    <span className="text-red-400 line-through">{detail.reconciled_from}</span>
                    <span className="text-gray-500"> → </span>
                    <span className="text-green-400">{detail.reconciled_to}</span>
                  </>
                )}
                {detail.reason && (
                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                    detail.reason === 'orphan_fallback' ? 'bg-red-900/50 text-red-300' :
                    detail.reason.includes('ambiguous') ? 'bg-amber-900/50 text-amber-300' :
                    'bg-blue-900/50 text-blue-300'
                  }`}>
                    {detail.reason.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
            ))}
            {/* Fallback for old cases */}
            {!log.reconciliation_details && log.location_mapping
              ?.filter(m => m.reconciled_from)
              .map((mapping, idx) => (
                <p key={idx} className="text-sm text-blue-300">
                  {mapping.suspect_name}: <span className="text-red-400 line-through">{mapping.reconciled_from}</span> → <span className="text-green-400">{mapping.reconciled_to}</span>
                </p>
              ))}
          </div>
          {/* Ambiguous suspects detail */}
          {log.ambiguous_suspects?.length > 0 && (
            <div className="mt-2 bg-amber-900/20 rounded p-2 border border-amber-700/50">
              <p className="text-xs text-amber-400 mb-1">Suspects in multiple locations (first chosen):</p>
              {log.ambiguous_suspects.map((amb, idx) => (
                <p key={idx} className="text-sm text-amber-300">
                  {amb.suspect_name}: found in [{amb.found_in?.join(', ')}] → chose <span className="text-green-400">{amb.chosen}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Evidence Reconciliation */}
      {(log.clue_location_fixes?.length > 0 || log.known_evidence_location_fixes?.length > 0 || log.discoverability_issues?.length > 0) && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-purple-400 mb-2 flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Evidence Reconciliation
            {log.clue_location_fixes?.length > 0 && (
              <span className="text-xs bg-purple-800 px-1.5 py-0.5 rounded">{log.clue_location_fixes.length} clue fixes</span>
            )}
            {log.discoverability_issues?.length > 0 && (
              <span className="text-xs bg-red-800 px-1.5 py-0.5 rounded">{log.discoverability_issues.length} issues</span>
            )}
          </h4>

          {/* Clue Location Fixes */}
          {log.clue_location_fixes?.length > 0 && (
            <div className="bg-purple-900/20 rounded p-2 border border-purple-700/50 mb-2">
              <p className="text-xs text-purple-400 mb-1">Clue location assignments fixed:</p>
              {log.clue_location_fixes.map((fix, idx) => (
                <p key={idx} className="text-sm text-purple-300">
                  {fix.clue_id}: <span className="text-red-400 line-through">{fix.from || 'none'}</span> → <span className="text-green-400">{fix.to}</span>
                  {fix.skeleton_slot && fix.skeleton_slot !== 'fallback' && (
                    <span className="text-xs text-gray-500 ml-1">(slot: {fix.skeleton_slot})</span>
                  )}
                </p>
              ))}
            </div>
          )}

          {/* Known Evidence Fixes */}
          {log.known_evidence_location_fixes?.length > 0 && (
            <div className="bg-cyan-900/20 rounded p-2 border border-cyan-700/50 mb-2">
              <p className="text-xs text-cyan-400 mb-1">known_evidence.locations translated:</p>
              {log.known_evidence_location_fixes.map((fix, idx) => (
                <div key={idx} className="text-sm text-cyan-300">
                  <span className="font-medium">{fix.suspect_id}:</span>
                  {fix.fixes.map((f, i) => (
                    <span key={i} className="ml-2">
                      {f.from} → {f.to || <span className="text-red-400">removed</span>}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Discoverability Issues */}
          {log.discoverability_issues?.length > 0 && (
            <div className="bg-red-900/20 rounded p-2 border border-red-700/50">
              <p className="text-xs text-red-400 mb-1">Discoverability issues (auto-fixed):</p>
              {log.discoverability_issues.map((issue, idx) => (
                <p key={idx} className="text-sm text-red-300">
                  {issue.clue_id}: {issue.issue}
                  {issue.revealed_by && <span className="text-gray-500"> (revealed_by: {issue.revealed_by})</span>}
                  {issue.revealer_location && <span className="text-gray-500"> at {issue.revealer_location}</span>}
                </p>
              ))}
            </div>
          )}

          {/* Slot Mapping (collapsible) */}
          {log.slot_to_location_mapping && (
            <details className="mt-2">
              <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">
                Show slot → location ID mapping
              </summary>
              <pre className="mt-1 text-xs text-gray-400 bg-black/30 p-2 rounded overflow-x-auto">
                {JSON.stringify(log.slot_to_location_mapping, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}

      {/* Warnings */}
      {log.warnings?.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Warnings ({log.warnings.length})
          </h4>
          <div className="bg-amber-900/20 rounded p-2 border border-amber-700/50">
            {log.warnings.map((warning, idx) => (
              <p key={idx} className="text-sm text-amber-300">{warning}</p>
            ))}
          </div>
        </div>
      )}

      {/* Location Mapping Details */}
      <details className="mt-2">
        <summary className="text-sm text-gray-400 cursor-pointer hover:text-gray-300">
          Show location mapping details
        </summary>
        <pre className="mt-2 text-xs text-gray-400 bg-black/30 p-3 rounded overflow-x-auto">
          {JSON.stringify(log.location_mapping, null, 2)}
        </pre>
      </details>

      {/* Skeleton Locations */}
      <details className="mt-2">
        <summary className="text-sm text-gray-400 cursor-pointer hover:text-gray-300">
          Show skeleton locations
        </summary>
        <pre className="mt-2 text-xs text-gray-400 bg-black/30 p-3 rounded overflow-x-auto">
          {JSON.stringify(log.skeleton_locations, null, 2)}
        </pre>
      </details>
    </div>
  );
};

/**
 * SkeletonMetadataSection - Collapsible skeleton metadata JSON
 */
const SkeletonMetadataSection = ({ metadata }) => {
  if (!metadata) return null;

  return (
    <details className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
      <summary className="text-lg font-semibold text-white cursor-pointer">
        Skeleton Metadata
      </summary>
      <pre className="mt-3 text-xs text-gray-400 bg-black/30 p-3 rounded overflow-x-auto">
        {JSON.stringify(metadata, null, 2)}
      </pre>
    </details>
  );
};

/**
 * NoGenerationLogWarning - Shown when case has no generation log
 */
const NoGenerationLogWarning = () => (
  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 text-center">
    <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
    <p className="text-gray-400">No generation log available for this case.</p>
    <p className="text-sm text-gray-500">Cases generated before this feature won't have logs.</p>
  </div>
);

/**
 * GenerationInfoContent - All generation info sections combined
 */
const GenerationInfoContent = ({ data }) => (
  <div className="space-y-6">
    <CaseOverviewSection data={data} />
    <SuspectLocationsSection suspects={data.suspects} />
    <GenerationLogSection log={data.generation_log} />
    <SkeletonMetadataSection metadata={data.skeleton_metadata} />
    {!data.generation_log && <NoGenerationLogWarning />}
  </div>
);

const EmptyState = ({ message }) => (
  <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-8 text-center text-gray-400">
    {message}
  </div>
);

const DetailGrid = ({ rows }) => {
  const visibleRows = rows.filter((row) => row.value !== undefined && row.value !== null && row.value !== '');

  if (visibleRows.length === 0) {
    return <EmptyState message="No details available for this section." />;
  }

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {visibleRows.map((row) => (
        <div key={row.label} className="rounded-md border border-gray-800 bg-gray-950/50 p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{row.label}</div>
          <div className="mt-1 text-sm text-gray-200" title={row.title || undefined}>
            {String(row.value)}
          </div>
        </div>
      ))}
    </div>
  );
};

const IdentityContent = ({ data }) => {
  const victim = data?.victim || {};
  const guide = data?.guide || data?.detective_guide || data?.game_master || {};
  const suspects = Array.isArray(data?.suspects) ? data.suspects : [];

  if (!victim.name && !guide.name && suspects.length === 0) {
    return <EmptyState message="No identity metadata is available for this case." />;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
        <h3 className="mb-3 text-lg font-semibold text-white">Victim Identity</h3>
        <DetailGrid rows={[
          { label: 'Name', value: victim.name || data?.victim_name },
          { label: 'Gender', value: victim.gender },
          { label: 'Pronouns', value: victim.pronouns },
          { label: 'Age', value: victim.age },
          { label: 'Occupation', value: victim.occupation },
        ]} />
      </div>

      {(guide.name || guide.gender || guide.pronouns) && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
          <h3 className="mb-3 text-lg font-semibold text-white">Guide Identity</h3>
          <DetailGrid rows={[
            { label: 'Name', value: guide.name },
            { label: 'Gender', value: guide.gender },
            { label: 'Pronouns', value: guide.pronouns },
            { label: 'Role', value: guide.role || guide.occupation },
          ]} />
        </div>
      )}

      {suspects.length > 0 && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
          <h3 className="mb-3 text-lg font-semibold text-white">Suspect Identities</h3>
          <div className="space-y-2">
            {suspects.map((suspect) => (
              <div key={suspect.id || suspect.name} className="rounded-md border border-gray-800 bg-gray-950/50 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-white">{suspect.name || 'Unknown suspect'}</span>
                  {suspect.is_killer && <Badge className="bg-red-600 text-white text-xs">KILLER</Badge>}
                </div>
                <div className="mt-2 grid gap-2 text-sm text-gray-400 md:grid-cols-3">
                  <span>Gender: <span className="text-gray-200">{suspect.gender || '-'}</span></span>
                  <span>Pronouns: <span className="text-gray-200">{suspect.pronouns || '-'}</span></span>
                  <span>Age: <span className="text-gray-200">{suspect.age || '-'}</span></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const LogicContent = ({ data }) => {
  const log = data?.generation_log || {};
  const warnings = log.warnings || [];
  const discoverabilityIssues = log.discoverability_issues || [];
  const reconciliationDetails = log.reconciliation_details || [];

  if (!data?.generation_log && warnings.length === 0 && discoverabilityIssues.length === 0) {
    return <EmptyState message="No semantic logic summary is available for this case." />;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
        <h3 className="mb-3 text-lg font-semibold text-white">Logic Signals</h3>
        <DetailGrid rows={[
          { label: 'Generation Mode', value: data?.generation_mode },
          { label: 'Crime Scene Suspects', value: data?.crime_scene_suspect_count },
          { label: 'ID Match Rate', value: log.id_match_rate },
          { label: 'Fallbacks', value: log.has_fallbacks === undefined ? undefined : (log.has_fallbacks ? 'Yes' : 'No') },
          { label: 'Warnings', value: warnings.length },
          { label: 'Discoverability Issues', value: discoverabilityIssues.length },
        ]} />
      </div>

      {(warnings.length > 0 || discoverabilityIssues.length > 0 || reconciliationDetails.length > 0) && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
          <h3 className="mb-3 text-lg font-semibold text-white">Open Logic Details</h3>
          <div className="space-y-3">
            {warnings.map((warning, index) => (
              <p key={`warning-${index}`} className="rounded-md border border-amber-800 bg-amber-900/20 p-2 text-sm text-amber-300">
                {warning}
              </p>
            ))}
            {discoverabilityIssues.map((issue, index) => (
              <p key={`discoverability-${index}`} className="rounded-md border border-red-800 bg-red-900/20 p-2 text-sm text-red-300">
                {issue.clue_id || issue.clue_name || 'clue'}: {issue.issue || issue.issue_type || 'discoverability issue'}
              </p>
            ))}
            {reconciliationDetails.slice(0, 12).map((detail, index) => (
              <p key={`reconciliation-${index}`} className="rounded-md border border-blue-800 bg-blue-900/20 p-2 text-sm text-blue-300">
                {detail.suspect_name || detail.clue_id || 'item'}: {detail.reason || 'reconciled'}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CipherContent = ({ data }) => {
  const cipher = data?.cipher_summary || data?.cipher || data?.cipher_metadata || data?.cipher_info || data?.generation_log?.cipher || {};
  const allowedFields = [
    'exists',
    'cipher_exists',
    'type',
    'cipher_type',
    'unlock_location',
    'hints_total',
  ];
  const rows = allowedFields
    .filter((key) => cipher[key] !== undefined && cipher[key] !== null && cipher[key] !== '')
    .map((key) => ({ label: key.replace(/_/g, ' '), value: cipher[key] }));
  const issues = data?.generation_log?.cipher_issues || cipher.issues || [];

  if (rows.length === 0 && issues.length === 0) {
    return <EmptyState message="No safe cipher summary is available for this case. Cipher secrets are intentionally hidden." />;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
        <h3 className="mb-3 text-lg font-semibold text-white">Cipher Summary</h3>
        <DetailGrid rows={rows} />
        <p className="mt-3 text-xs text-gray-500">Cipher solutions, unlock payloads, and reward contents are not shown here.</p>
      </div>
      {issues.length > 0 && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
          <h3 className="mb-3 text-lg font-semibold text-white">Cipher Issues</h3>
          <div className="space-y-2">
            {issues.map((issue, index) => (
              <p key={index} className="rounded-md border border-amber-800 bg-amber-900/20 p-2 text-sm text-amber-300">
                {typeof issue === 'string' ? issue : issue.issue || issue.message || 'Cipher issue'}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SessionsContent = ({ data, sessionId }) => {
  const sessions = Array.isArray(data?.sessions) ? data.sessions : [];
  const createdById = data?.created_by || data?.created_by_user_id;
  const createdByLabel = getUserDisplayLabel({
    id: createdById,
    displayName: data?.created_by_display_name,
  });

  if (!sessionId && !data?.session_id && sessions.length === 0) {
    return <EmptyState message="No session metadata is available for this case." />;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
        <h3 className="mb-3 text-lg font-semibold text-white">Current Session</h3>
        <DetailGrid rows={[
          { label: 'Session ID', value: sessionId || data?.session_id },
          { label: 'Game Status', value: data?.game_status },
          { label: 'Turn Count', value: data?.turn_count },
          { label: 'Created By', value: createdByLabel, title: createdById },
        ]} />
      </div>

      {sessions.length > 0 && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
          <h3 className="mb-3 text-lg font-semibold text-white">Related Sessions</h3>
          <div className="space-y-2">
            {sessions.map((session) => (
              <div key={session.session_id || session.id} className="rounded-md border border-gray-800 bg-gray-950/50 p-3 text-sm text-gray-300">
                <div className="font-mono text-xs text-gray-500">{session.session_id || session.id}</div>
                {(session.user_id || session.user_display_name) && (
                  <div className="mt-1">
                    User:{' '}
                    <span className="text-gray-200" title={session.user_id || undefined}>
                      {getUserDisplayLabel({
                        id: session.user_id,
                        displayName: session.user_display_name,
                      })}
                    </span>
                  </div>
                )}
                <div className="mt-1">Status: <span className="text-gray-200">{session.status || session.game_status || '-'}</span></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// DIAGNOSTICS COMPONENTS
// =============================================================================

/**
 * DiagnosticsHeader - Summary information at the top
 */
const DiagnosticsHeader = ({ data }) => (
  <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
      <div>
        <span className="text-gray-400">Session:</span>
        <span className="ml-2 text-white font-mono text-xs">{data.session_id?.slice(-8)}</span>
      </div>
      <div>
        <span className="text-gray-400">Status:</span>
        <Badge className="ml-2">{data.game_status}</Badge>
      </div>
      <div>
        <span className="text-gray-400">Turns:</span>
        <span className="ml-2 text-white">{data.turn_count}</span>
      </div>
      <div>
        <span className="text-gray-400">Analyzed:</span>
        <span className="ml-2 text-gray-300 text-xs">
          {data.analysis_timestamp ? new Date(data.analysis_timestamp).toLocaleString() : 'N/A'}
        </span>
      </div>
    </div>
  </div>
);

/**
 * ProceduralSkeletonSection - Shows if procedural skeleton fired or fell back to LLM
 */
const ProceduralSkeletonSection = ({ procedural }) => {
  const fired = procedural?.skeleton_fired;

  return (
    <details className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden" open={!fired}>
      <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-800/70">
        <div className="flex items-center gap-3">
          <CheckCircle className={`w-5 h-5 ${fired ? 'text-green-400' : 'text-amber-400'}`} />
          <span className="font-semibold text-white">Procedural Skeleton</span>
        </div>
        <Badge className={fired ? 'bg-green-900/50 text-green-300' : 'bg-amber-900/50 text-amber-300'}>
          {fired ? 'FIRED' : 'FALLBACK'}
        </Badge>
      </summary>
      <div className="p-4 pt-0 border-t border-gray-700 text-sm space-y-2">
        <p><span className="text-gray-400">Mode:</span> <span className="text-white">{procedural?.generation_mode}</span></p>
        {procedural?.fallback_reason && (
          <p><span className="text-gray-400">Reason:</span> <span className="text-amber-300">{procedural.fallback_reason}</span></p>
        )}
      </div>
    </details>
  );
};

/**
 * SolvabilitySection - Shows if the case is solvable
 */
const SolvabilitySection = ({ solvability }) => {
  const isSolvable = solvability?.is_solvable;

  return (
    <details className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden" open={!isSolvable}>
      <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-800/70">
        <div className="flex items-center gap-3">
          {isSolvable ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : (
            <XCircle className="w-5 h-5 text-red-400" />
          )}
          <span className="font-semibold text-white">Case Solvability</span>
        </div>
        <Badge className={isSolvable ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'}>
          {isSolvable ? 'SOLVABLE' : 'NOT SOLVABLE'}
        </Badge>
      </summary>
      <div className="p-4 pt-0 border-t border-gray-700 text-sm space-y-2">
        <p><span className="text-gray-400">Killer:</span> <span className="text-white">{solvability?.killer_name || 'Unknown'}</span></p>
        <div className="flex flex-wrap gap-2 mt-2">
          <Badge className={solvability?.killer_reachable ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'}>
            Reachable: {solvability?.killer_reachable ? 'Yes' : 'No'}
          </Badge>
          <Badge className={solvability?.critical_clues_exist ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'}>
            Critical Clues: {solvability?.critical_clues_exist ? 'Yes' : 'No'}
          </Badge>
          <Badge className={solvability?.alibi_flaw_discoverable ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'}>
            Alibi Flaw: {solvability?.alibi_flaw_discoverable ? 'Yes' : 'No'}
          </Badge>
        </div>
        {solvability?.issues?.length > 0 && (
          <div className="mt-3 p-2 bg-red-900/20 rounded border border-red-700/50">
            <p className="text-red-400 font-medium mb-1">Issues:</p>
            {solvability.issues.map((issue, i) => (
              <p key={i} className="text-red-300 text-xs">- {issue}</p>
            ))}
          </div>
        )}
      </div>
    </details>
  );
};

/**
 * DialogueConsistencySection - Shows dialogue consistency score and issues
 */
const DialogueConsistencySection = ({ dialogue }) => {
  const score = dialogue?.consistency_score || 0;
  const scoreColor = score >= 80 ? 'text-green-400' : score >= 50 ? 'text-amber-400' : 'text-red-400';
  const badgeColor = score >= 80 ? 'bg-green-900/50 text-green-300' : score >= 50 ? 'bg-amber-900/50 text-amber-300' : 'bg-red-900/50 text-red-300';

  return (
    <details className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
      <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-800/70">
        <div className="flex items-center gap-3">
          <CheckCircle className={`w-5 h-5 ${scoreColor}`} />
          <span className="font-semibold text-white">Dialogue Consistency</span>
        </div>
        <Badge className={badgeColor}>
          {score.toFixed(0)}/100
        </Badge>
      </summary>
      <div className="p-4 pt-0 border-t border-gray-700 text-sm space-y-2">
        <p><span className="text-gray-400">Interviews:</span> <span className="text-white">{dialogue?.total_interviews || 0}</span></p>
        {dialogue?.issues_found?.length > 0 && (
          <div className="mt-2">
            <p className="text-gray-400 mb-1">Issues ({dialogue.issues_found.length}):</p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {dialogue.issues_found.slice(0, 10).map((issue, i) => (
                <div key={i} className="text-xs p-2 bg-gray-700/50 rounded">
                  <span className={`font-medium ${
                    issue.severity === 'high' ? 'text-red-400' :
                    issue.severity === 'medium' ? 'text-amber-400' : 'text-gray-400'
                  }`}>[{issue.severity}]</span>
                  <span className="text-white ml-1">Turn {issue.turn}:</span>
                  <span className="text-gray-300 ml-1">{issue.issue_type}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </details>
  );
};

/**
 * ContentIntegritySection - Shows content integrity score and issue breakdown
 */
const ContentIntegritySection = ({ contentIntegrity }) => {
  const score = contentIntegrity?.integrity_score ?? 100;
  const hasIssues = score < 80;
  const scoreColor = score >= 80 ? 'text-green-400' : score >= 50 ? 'text-amber-400' : 'text-red-400';
  const badgeColor = score >= 80 ? 'bg-green-900/50 text-green-300' : score >= 50 ? 'bg-amber-900/50 text-amber-300' : 'bg-red-900/50 text-red-300';

  return (
    <details className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden" open={hasIssues}>
      <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-800/70">
        <div className="flex items-center gap-3">
          {score >= 80 ? (
            <CheckCircle className={`w-5 h-5 ${scoreColor}`} />
          ) : (
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          )}
          <span className="font-semibold text-white">Content Integrity</span>
        </div>
        <Badge className={badgeColor}>
          {score.toFixed(0)}/100
        </Badge>
      </summary>
      <div className="p-4 pt-0 border-t border-gray-700 text-sm space-y-2">
        <p><span className="text-gray-400">Total Issues:</span> <span className="text-white">{contentIntegrity?.issues?.length || 0}</span></p>
        {contentIntegrity?.name_issues?.length > 0 && (
          <div className="p-2 bg-red-900/20 rounded border border-red-700/50">
            <p className="text-red-400 text-xs font-medium">Name Issues: {contentIntegrity.name_issues.length}</p>
          </div>
        )}
        {contentIntegrity?.cipher_issues?.length > 0 && (
          <div className="p-2 bg-red-900/20 rounded border border-red-700/50">
            <p className="text-red-400 text-xs font-medium">Cipher Leakage: {contentIntegrity.cipher_issues.length}</p>
          </div>
        )}
        {contentIntegrity?.narrative_issues?.length > 0 && (
          <div className="p-2 bg-amber-900/20 rounded border border-amber-700/50">
            <p className="text-amber-400 text-xs font-medium">ID Leakage: {contentIntegrity.narrative_issues.length}</p>
          </div>
        )}
      </div>
    </details>
  );
};

/**
 * StuckDetectionSection - Shows if player appears stuck
 */
const StuckDetectionSection = ({ stuck }) => {
  const isStuck = stuck?.likely_stuck;

  return (
    <details className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden" open={isStuck}>
      <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-800/70">
        <div className="flex items-center gap-3">
          {isStuck ? (
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          ) : (
            <CheckCircle className="w-5 h-5 text-green-400" />
          )}
          <span className="font-semibold text-white">Stuck Detection</span>
        </div>
        <Badge className={isStuck ? 'bg-amber-900/50 text-amber-300' : 'bg-green-900/50 text-green-300'}>
          {isStuck ? 'LIKELY STUCK' : 'OK'}
        </Badge>
      </summary>
      <div className="p-4 pt-0 border-t border-gray-700 text-sm space-y-2">
        {isStuck && (
          <>
            <p><span className="text-gray-400">Stuck at turn:</span> <span className="text-amber-300">{stuck.stuck_at_turn}</span></p>
            <p><span className="text-gray-400">Reason:</span> <span className="text-amber-300">{stuck.stuck_reason}</span></p>
          </>
        )}
        {stuck?.progress_metrics && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            {Object.entries(stuck.progress_metrics).map(([key, val]) => (
              <div key={key} className="text-xs">
                <span className="text-gray-500">{key.replace(/_/g, ' ')}:</span> <span className="text-gray-300">{String(val)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </details>
  );
};

/**
 * ClueRevelationSection - Shows clue discovery audit results
 */
const ClueRevelationSection = ({ clueRevelation }) => (
  <details className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
    <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-800/70">
      <div className="flex items-center gap-3">
        <CheckCircle className="w-5 h-5 text-blue-400" />
        <span className="font-semibold text-white">Clue Revelation Audit</span>
      </div>
      <Badge className="bg-blue-900/50 text-blue-300">
        {clueRevelation?.discovered_clues || 0}/{clueRevelation?.total_clues || 0}
      </Badge>
    </summary>
    <div className="p-4 pt-0 border-t border-gray-700 text-sm space-y-2">
      <p><span className="text-gray-400">Properly Revealed:</span> <span className="text-white">{clueRevelation?.properly_revealed || 0}</span></p>
      {clueRevelation?.issues?.length > 0 && (
        <div className="mt-2">
          <p className="text-gray-400 mb-1">Issues ({clueRevelation.issues.length}):</p>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {clueRevelation.issues.slice(0, 5).map((issue, i) => (
              <div key={i} className="text-xs p-2 bg-amber-900/20 rounded">
                <span className="text-amber-400">{issue.clue_name || issue.clue_id}:</span>
                <span className="text-gray-300 ml-1">{issue.issue_type || issue.issue}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </details>
);

/**
 * ConsultEffectivenessSection - Shows guide character consultation effectiveness
 */
const ConsultEffectivenessSection = ({ consult }) => {
  const effectiveness = consult?.overall_effectiveness || 'N/A';
  const iconColor = effectiveness === 'helpful' ? 'text-green-400' : effectiveness === 'not_used' ? 'text-gray-400' : 'text-amber-400';
  const badgeColor = effectiveness === 'helpful' ? 'bg-green-900/50 text-green-300' : effectiveness === 'not_used' ? 'bg-gray-700 text-gray-300' : 'bg-amber-900/50 text-amber-300';

  return (
    <details className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
      <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-800/70">
        <div className="flex items-center gap-3">
          <Info className={`w-5 h-5 ${iconColor}`} />
          <span className="font-semibold text-white">Consult Effectiveness</span>
        </div>
        <Badge className={badgeColor}>
          {effectiveness.toUpperCase().replace(/_/g, ' ')}
        </Badge>
      </summary>
      <div className="p-4 pt-0 border-t border-gray-700 text-sm">
        <p><span className="text-gray-400">Used:</span> <span className="text-white">{consult?.consults_used || 0}/{consult?.consult_limit || 0}</span></p>
      </div>
    </details>
  );
};

/**
 * CipherAnalysisSection - Shows cipher puzzle status (only if cipher exists)
 */
const CipherAnalysisSection = ({ cipher }) => {
  if (!cipher?.cipher_exists) return null;

  const status = cipher.solved ? 'SOLVED' : cipher.unlocked ? 'UNLOCKED' : 'LOCKED';
  const badgeColor = cipher.solved ? 'bg-green-900/50 text-green-300' : 'bg-blue-900/50 text-blue-300';

  return (
    <details className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
      <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-800/70">
        <div className="flex items-center gap-3">
          {cipher.solved ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : (
            <Info className="w-5 h-5 text-blue-400" />
          )}
          <span className="font-semibold text-white">Cipher Analysis</span>
        </div>
        <Badge className={badgeColor}>{status}</Badge>
      </summary>
      <div className="p-4 pt-0 border-t border-gray-700 text-sm space-y-2">
        <p><span className="text-gray-400">Type:</span> <span className="text-white">{cipher.cipher_type || 'Unknown'}</span></p>
        <p><span className="text-gray-400">Hints Found:</span> <span className="text-white">{cipher.hints_found || 0}/{cipher.hints_total || 0}</span></p>
        <p><span className="text-gray-400">Attempts:</span> <span className="text-white">{cipher.attempts || 0}</span></p>
        {cipher.issues?.length > 0 && (
          <div className="p-2 bg-amber-900/20 rounded mt-2 border border-amber-700/50">
            {cipher.issues.map((issue, i) => (
              <p key={i} className="text-amber-300 text-xs">- {issue}</p>
            ))}
          </div>
        )}
      </div>
    </details>
  );
};

/**
 * SummaryHallucinationSection - Shows summary grounding check (only if summary exists)
 */
const SummaryHallucinationSection = ({ summary }) => {
  if (!summary?.summary_exists) return null;

  const score = summary.grounded_score || 0;
  const isGood = score >= 80;
  const badgeColor = isGood ? 'bg-green-900/50 text-green-300' : 'bg-amber-900/50 text-amber-300';

  return (
    <details className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
      <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-800/70">
        <div className="flex items-center gap-3">
          {isGood ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          )}
          <span className="font-semibold text-white">Summary Hallucination Check</span>
        </div>
        <Badge className={badgeColor}>
          {score.toFixed(0)}/100
        </Badge>
      </summary>
      <div className="p-4 pt-0 border-t border-gray-700 text-sm">
        {summary.hallucinations_found?.length > 0 ? (
          <div className="space-y-1">
            {summary.hallucinations_found.map((h, i) => (
              <div key={i} className="text-xs p-2 bg-amber-900/20 rounded">
                <span className="text-amber-400">[{h.severity}]</span>
                <span className="text-white ml-1">{h.field}:</span>
                <span className="text-gray-300 ml-1">{h.found}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-green-400">Summary is well-grounded in case data.</p>
        )}
      </div>
    </details>
  );
};

/**
 * DiagnosticsContent - Renders all diagnostic sections
 */
const DiagnosticsContent = ({ data }) => (
  <div className="space-y-4">
    <DiagnosticsHeader data={data} />
    <ProceduralSkeletonSection procedural={data.procedural} />
    <SolvabilitySection solvability={data.solvability} />
    <DialogueConsistencySection dialogue={data.dialogue} />
    <ContentIntegritySection contentIntegrity={data.content_integrity} />
    <StuckDetectionSection stuck={data.stuck} />
    <ClueRevelationSection clueRevelation={data.clue_revelation} />
    <ConsultEffectivenessSection consult={data.consult} />
    <CipherAnalysisSection cipher={data.cipher} />
    <SummaryHallucinationSection summary={data.summary} />
  </div>
);

// =============================================================================
// MAIN MODAL COMPONENT
// =============================================================================

/**
 * CaseInfoModal - Combined admin modal for case generation info and session diagnostics
 *
 * Props:
 * - isOpen: boolean - whether the modal is visible
 * - onClose: () => void - callback to close the modal
 * - caseId: string - the case ID to fetch generation info for
 * - sessionId: string - the session ID to fetch diagnostics for
 * - onError: (message: string) => void - callback for error handling (toast)
 */
const CaseInfoModalComponent = ({ isOpen, onClose, caseId, sessionId, onError }) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);
  const currentCaseIdRef = useRef(caseId);
  currentCaseIdRef.current = caseId;

  // Tab state
  const [activeTab, setActiveTab] = useState(TABS.SUMMARY);

  // Case detail state
  const [caseDetails, setCaseDetails] = useState(null);
  const [caseDetailsLoading, setCaseDetailsLoading] = useState(false);
  const [review, setReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewLoaded, setReviewLoaded] = useState(false);
  const [imageAuditRows, setImageAuditRows] = useState([]);
  const [imageAuditTotal, setImageAuditTotal] = useState(0);
  const [imageAuditLoading, setImageAuditLoading] = useState(false);
  const [generationFailures, setGenerationFailures] = useState([]);
  const [generationFailuresTotal, setGenerationFailuresTotal] = useState(0);
  const [generationFailuresLoading, setGenerationFailuresLoading] = useState(false);

  // Diagnostics state
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [diagnosticsFetched, setDiagnosticsFetched] = useState(false);
  const [diagnosticsSource, setDiagnosticsSource] = useState({ caseId: null, sessionId: null });
  const effectiveSessionId = sessionId || caseDetails?.session_id || null;

  // Handle backdrop click to close modal
  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Fetch case detail review data when modal opens
  useEffect(() => {
    if (!isOpen || !caseId) return;

    let ignore = false;

    const fetchCaseDetails = async () => {
      setCaseDetails(null);
      setReview(null);
      setReviewLoaded(false);
      setImageAuditRows([]);
      setImageAuditTotal(0);
      setGenerationFailures([]);
      setGenerationFailuresTotal(0);
      setCaseDetailsLoading(true);
      setReviewLoading(true);
      setImageAuditLoading(true);
      setGenerationFailuresLoading(true);
      try {
        const [
          caseDetailsResult,
          reviewResult,
          imageAuditResult,
          generationFailureResult,
        ] = await Promise.allSettled([
          gameAPI.getCaseAdminDetails(caseId),
          gameAPI.getCaseReview(caseId),
          gameAPI.getCaseImageAudit(caseId),
          gameAPI.getAdminGenerationFailures({ case_id: caseId, limit: 100 }),
        ]);

        if (ignore) return;

        if (caseDetailsResult.status === 'fulfilled') {
          setCaseDetails(caseDetailsResult.value);
        } else {
          console.error('Failed to fetch case details:', caseDetailsResult.reason);
          onError?.(caseDetailsResult.reason?.response?.data?.detail || caseDetailsResult.reason?.message || 'Could not retrieve case details');
        }

        if (reviewResult.status === 'fulfilled') {
          setReview(reviewResult.value);
          setReviewLoaded(true);
        } else {
          console.error('Failed to fetch case review:', reviewResult.reason);
          onError?.(reviewResult.reason?.response?.data?.detail || reviewResult.reason?.message || 'Could not retrieve case review metadata');
        }

        if (imageAuditResult.status === 'fulfilled') {
          setImageAuditRows(imageAuditResult.value.images || []);
          setImageAuditTotal(imageAuditResult.value.total || 0);
        } else {
          console.error('Failed to fetch case image audit:', imageAuditResult.reason);
          onError?.(imageAuditResult.reason?.response?.data?.detail || imageAuditResult.reason?.message || 'Could not retrieve case image audit');
        }

        if (generationFailureResult.status === 'fulfilled') {
          setGenerationFailures(generationFailureResult.value.failures || []);
          setGenerationFailuresTotal(generationFailureResult.value.total || 0);
        } else {
          console.error('Failed to fetch generation failures:', generationFailureResult.reason);
          onError?.(generationFailureResult.reason?.response?.data?.detail || generationFailureResult.reason?.message || 'Could not retrieve generation failures');
        }
      } catch (error) {
        if (ignore) return;
        console.error('Failed to fetch case details:', error);
        onError?.(error.response?.data?.detail || error.message || 'Could not retrieve case details');
      } finally {
        if (!ignore) {
          setCaseDetailsLoading(false);
          setReviewLoading(false);
          setImageAuditLoading(false);
          setGenerationFailuresLoading(false);
        }
      }
    };

    fetchCaseDetails();

    return () => {
      ignore = true;
    };
  }, [isOpen, caseId, onError]);

  // Fetch diagnostics when tab is switched (lazy loading)
  useEffect(() => {
    if (!isOpen || activeTab !== TABS.DIAGNOSTICS || !effectiveSessionId || diagnosticsFetched) return;

    let ignore = false;

    const fetchDiagnostics = async () => {
      setDiagnosticsLoading(true);
      try {
        const data = await gameAPI.getSessionDiagnostics(effectiveSessionId);
        if (ignore) return;
        setDiagnostics(data);
        setDiagnosticsSource({ caseId, sessionId: effectiveSessionId });
        setDiagnosticsFetched(true);
      } catch (error) {
        if (ignore) return;
        console.error('Failed to run diagnostics:', error);
        onError?.(error.response?.data?.detail || error.message || 'Could not run diagnostics');
      } finally {
        if (!ignore) {
          setDiagnosticsLoading(false);
        }
      }
    };

    fetchDiagnostics();

    return () => {
      ignore = true;
    };
  }, [isOpen, activeTab, caseId, effectiveSessionId, diagnosticsFetched, onError]);

  // Reset diagnostics when the modal context changes while it remains open.
  useEffect(() => {
    if (!isOpen) return;
    setDiagnostics(null);
    setDiagnosticsSource({ caseId: null, sessionId: null });
    setDiagnosticsFetched(false);
  }, [isOpen, caseId, effectiveSessionId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCaseDetails(null);
      setReview(null);
      setReviewLoaded(false);
      setImageAuditRows([]);
      setImageAuditTotal(0);
      setGenerationFailures([]);
      setGenerationFailuresTotal(0);
      setDiagnostics(null);
      setDiagnosticsSource({ caseId: null, sessionId: null });
      setDiagnosticsFetched(false);
      setActiveTab(TABS.SUMMARY);
    }
  }, [isOpen]);

  const handleSaveReview = useCallback(async (reviewUpdate) => {
    const requestCaseId = caseId;
    try {
      const updatedReview = await gameAPI.updateCaseReview(requestCaseId, reviewUpdate);
      if (currentCaseIdRef.current === requestCaseId) {
        setReview(updatedReview);
      }
      return updatedReview;
    } catch (error) {
      console.error('Failed to update case review:', error);
      onError?.(error.response?.data?.detail || error.message || 'Could not update case review');
      throw error;
    }
  }, [caseId, onError]);

  const handleAddReviewNote = useCallback(async (note) => {
    const requestCaseId = caseId;
    try {
      const updatedReview = await gameAPI.addCaseReviewNote(requestCaseId, note);
      if (currentCaseIdRef.current === requestCaseId) {
        setReview(updatedReview);
      }
      return updatedReview;
    } catch (error) {
      console.error('Failed to add case review note:', error);
      onError?.(error.response?.data?.detail || error.message || 'Could not add case review note');
      throw error;
    }
  }, [caseId, onError]);

  const handleTabKeyDown = useCallback((event) => {
    const navigationKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!navigationKeys.includes(event.key)) return;

    event.preventDefault();
    const currentIndex = TAB_ITEMS.findIndex((tab) => tab.id === activeTab);
    let nextIndex = currentIndex >= 0 ? currentIndex : 0;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (nextIndex + 1) % TAB_ITEMS.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (nextIndex - 1 + TAB_ITEMS.length) % TAB_ITEMS.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = TAB_ITEMS.length - 1;
    }

    const nextTab = TAB_ITEMS[nextIndex];
    setActiveTab(nextTab.id);
    window.requestAnimationFrame(() => {
      document.getElementById(`case-info-tab-${nextTab.id}`)?.focus();
    });
  }, [activeTab]);

  // Keyboard handling and focus management
  useEffect(() => {
    if (!isOpen) return;

    previousActiveElement.current = document.activeElement;
    if (modalRef.current) {
      modalRef.current.focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previousActiveElement.current && previousActiveElement.current.focus) {
        previousActiveElement.current.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const renderCaseDetailsEmpty = (message = 'No case details available.') => (
    <div className="text-center py-12 text-gray-400">{message}</div>
  );

  const renderCaseDetailsLoading = (message = 'Loading case details...') => (
    <div className="flex flex-col items-center justify-center py-12 gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      <p className="text-gray-400 text-sm">{message}</p>
    </div>
  );

  const diagnosticsMatchesCurrentContext =
    diagnosticsSource.caseId === caseId && diagnosticsSource.sessionId === effectiveSessionId;

  const renderTabContent = () => {
    switch (activeTab) {
      case TABS.SUMMARY:
        if (caseDetailsLoading) return renderCaseDetailsLoading('Loading case details...');
        return caseDetails ? <GenerationInfoContent data={caseDetails} /> : renderCaseDetailsEmpty();

      case TABS.REVIEW:
        if (!reviewLoading && !reviewLoaded) {
          return renderCaseDetailsEmpty('Review metadata could not be loaded.');
        }
        return (
          <ReviewStatusPanel
            caseId={caseId}
            review={review}
            loading={reviewLoading}
            disabled={!reviewLoaded}
            onSaveReview={handleSaveReview}
            onAddNote={handleAddReviewNote}
          />
        );

      case TABS.IDENTITY:
        if (caseDetailsLoading) return renderCaseDetailsLoading('Loading identity metadata...');
        return caseDetails ? <IdentityContent data={caseDetails} /> : renderCaseDetailsEmpty('No identity data available.');

      case TABS.LOGIC:
        if (caseDetailsLoading) return renderCaseDetailsLoading('Loading logic metadata...');
        return caseDetails ? <LogicContent data={caseDetails} /> : renderCaseDetailsEmpty('No logic data available.');

      case TABS.CIPHER:
        if (caseDetailsLoading) return renderCaseDetailsLoading('Loading cipher summary...');
        return caseDetails ? <CipherContent data={caseDetails} /> : renderCaseDetailsEmpty('No safe cipher summary available.');

      case TABS.IMAGES:
        return (
          <AdminImageAudit
            images={imageAuditRows}
            total={imageAuditTotal}
            loading={imageAuditLoading}
            filters={{ case_id: caseId, limit: 100, offset: 0 }}
            showControls={false}
          />
        );

      case TABS.FAILURES:
        return (
          <AdminGenerationFailures
            failures={generationFailures}
            total={generationFailuresTotal}
            loading={generationFailuresLoading}
            filters={{ case_id: caseId, limit: 100, offset: 0 }}
            showControls={false}
          />
        );

      case TABS.SESSIONS:
        if (caseDetailsLoading) return renderCaseDetailsLoading('Loading session metadata...');
        return caseDetails ? <SessionsContent data={caseDetails} sessionId={sessionId} /> : renderCaseDetailsEmpty('No session data available.');

      case TABS.DIAGNOSTICS:
        if (diagnosticsLoading) return renderCaseDetailsLoading('Running diagnostics...');
        return diagnostics && diagnosticsMatchesCurrentContext
          ? <DiagnosticsContent data={diagnostics} />
          : renderCaseDetailsEmpty('No diagnostics data available.');

      default:
        return renderCaseDetailsEmpty();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="case-info-modal-title"
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className="bg-gray-900 rounded-lg border border-gray-700 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col outline-none"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <Info className="w-6 h-6 text-amber-400" />
            <h2 id="case-info-modal-title" className="text-xl font-bold text-white">Case Info</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-700 overflow-x-auto">
          <div className="flex min-w-max" role="tablist" aria-label="Case info sections">
            {TAB_ITEMS.map((tab) => {
              const Icon = tab.icon;
              const selected = activeTab === tab.id;
              const isLoading =
                (tab.id === TABS.SUMMARY && caseDetailsLoading) ||
                (tab.id === TABS.REVIEW && reviewLoading) ||
                (tab.id === TABS.IMAGES && imageAuditLoading) ||
                (tab.id === TABS.FAILURES && generationFailuresLoading) ||
                (tab.id === TABS.DIAGNOSTICS && diagnosticsLoading);

              return (
                <button
                  key={tab.id}
                  className={`shrink-0 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${getTabClasses(tab, activeTab)}`}
	                  onClick={() => setActiveTab(tab.id)}
	                  onKeyDown={handleTabKeyDown}
	                  id={`case-info-tab-${tab.id}`}
	                  role="tab"
	                  aria-selected={selected}
	                  aria-controls={`case-info-panel-${tab.id}`}
	                  tabIndex={selected ? 0 : -1}
	                  type="button"
	                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {isLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Modal Content */}
        <div
          className="flex-1 overflow-y-auto p-4"
          id={`case-info-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`case-info-tab-${activeTab}`}
        >
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

// Wrap with React.memo to prevent unnecessary re-renders when parent re-renders
// with the same props (works with memoized callbacks from parent)
export const CaseInfoModal = React.memo(CaseInfoModalComponent);

export default CaseInfoModal;
